import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Dialog,
  Grid,
  MenuItem,
  Select,
  TextField,
  capitalize,
} from '@mui/material';

import { BinType } from 'commons/enums';
import { PRIMARY_RED } from 'commons/styles/colors';
import { ProductDetail } from 'commons/types';
import { getBinCommoditiesAction, postCreateBin } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import * as S from '../styles/CreateBinDialog.styles';
import { ICreateBinModalProps } from '../types';
import { isCommodityRequiredForBinType } from '../utils/binCommodityValidation';

export const CreateBinDialog: React.FC<ICreateBinModalProps> = ({
  onClose,
  openModal,
  location,
}) => {
  const [bin, setBin] = useState<Partial<ProductDetail>>({
    bin: '',
    bin_type: '',
    commodity: null,
  });

  const dispatch = useDispatch<AppDispatch>();

  const { enabledBinCommodityList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  useEffect(() => {
    dispatch(getBinCommoditiesAction({ isEnabled: true }));
  }, [dispatch]);

  const isCommodityMissing =
    isCommodityRequiredForBinType(bin.bin_type) && !bin.commodity;

  const isSubmitDisabled = !bin.bin || !bin.bin_type || isCommodityMissing;

  const onConfirm = async (): Promise<void> => {
    if (bin && bin.bin && bin.bin_type && !isCommodityMissing) {
      dispatch(
        postCreateBin({
          bin: {
            bin: bin.bin,
            bin_type: bin.bin_type,
            commodity: bin.commodity ?? null,
          },
          location,
        }),
      );
    }
  };

  const onFieldChange = (
    _type: keyof ProductDetail,
    value: string | null,
  ): void => {
    let newBin = structuredClone(bin);
    const newValue = typeof value === 'string' ? value.trim() : value;
    newBin = { ...newBin, [_type]: newValue };
    setBin(newBin);
  };

  return (
    <Dialog
      onClose={onClose}
      open={openModal}
      style={{ whiteSpace: 'pre-line' }}
    >
      <S.TitleWrapper>
        <S.Title data-testid="modalCreateBinHeader">Create New Bin</S.Title>
      </S.TitleWrapper>
      <Grid container style={{ marginTop: '12px', width: '450px' }}>
        <S.FieldRowWrapper container item xs={12}>
          <Grid item xs={4}>
            <S.Text>Bin Number</S.Text>
          </Grid>
          <S.InputWrapper>
            <TextField
              data-testid="inputCreateBinBinNumber"
              placeholder="Bin Number"
              variant="outlined"
              value={bin.bin || ''}
              onChange={(event): void =>
                onFieldChange('bin', event.target.value)
              }
              fullWidth
            />
          </S.InputWrapper>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper container item xs={12}>
          <Grid item xs={4}>
            <S.Text>Bin Type</S.Text>
          </Grid>
          <S.InputWrapper>
            <Select
              data-testid="dropdownCreateBinBinType"
              value={bin.bin_type || ''}
              onChange={(event): void =>
                onFieldChange('bin_type', event.target.value)
              }
              fullWidth
            >
              {Object.values(BinType)
                .filter(
                  (_binType) =>
                    ![BinType.PALLET, BinType.NONE].includes(_binType),
                )
                .map((_binType) => (
                  <MenuItem value={_binType}>
                    {capitalize(_binType.replace('_', ' '))}
                  </MenuItem>
                ))}
            </Select>
          </S.InputWrapper>
        </S.FieldRowWrapper>
        <S.FieldRowWrapper container item xs={12}>
          <Grid item xs={4}>
            <S.Text>Commodity</S.Text>
          </Grid>
          <S.InputWrapper>
            <Select
              data-testid="dropdownCreateBinCommodity"
              value={bin.commodity || ''}
              onChange={(event): void =>
                onFieldChange('commodity', event.target.value)
              }
              fullWidth
            >
              {enabledBinCommodityList.map((_commodity) => (
                <MenuItem value={_commodity.name}>{_commodity.name}</MenuItem>
              ))}
            </Select>
            {isCommodityMissing && (
              <p
                data-testid="textCreateBinCommodityRequired"
                style={{
                  color: PRIMARY_RED,
                  fontSize: '12px',
                  margin: '4px 0 0',
                }}
              >
                Commodity is required for this bin type
              </p>
            )}
          </S.InputWrapper>
        </S.FieldRowWrapper>
      </Grid>
      <S.FooterWrapper>
        <S.CancelButton data-testid="buttonCreateBinNoModal" onClick={onClose}>
          Cancel
        </S.CancelButton>
        <S.OKButton
          data-testid="buttonCreateBinYesModal"
          disabled={isSubmitDisabled}
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          Submit
        </S.OKButton>
      </S.FooterWrapper>
    </Dialog>
  );
};
